import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    FaCalendarAlt,
    FaSync,
    FaDownload,
    FaSearch,
    FaTimes,
    FaUserPlus,
    FaUserMinus,
    FaUserShield,
    FaInfoCircle,
    FaExclamationTriangle,
    FaChevronLeft,
    FaChevronRight,
    FaEnvelope,
    FaFileInvoiceDollar,
    FaSpinner,
    FaCamera,
} from 'react-icons/fa';
import { BLEEP_SERVER_NAMES, MONTHLY_SNAPSHOT_KINDS } from '../../utils/constants';
import {
    getBleepMonthlySnapshot,
    listBleepMonthlySnapshots,
    saveMonthlySnapshot,
} from '../../services/onboardingService';
import MonthlyLicenceSection from './MonthlyLicenceSection';
import MonthlyEmailAuditSection from './MonthlyEmailAuditSection';

// Formula injection protection for spreadsheet cells
const sanitizeCsvCell = (val) => {
    let s = String(val ?? '');
    if (/^[\s\u0000-\u001f]*[=+@-]/.test(s) || s.startsWith('\t') || s.startsWith('\r') || s.startsWith('\n')) {
        s = `'${s}`;
    }
    return `"${s.replace(/"/g, '""')}"`;
};

const formatDate = (val, includeTime = false) => {
    if (!val) return '—';
    try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val);
        const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        if (!includeTime) return dateStr;
        const timeStr = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')} UTC`;
        return `${dateStr} · ${timeStr}`;
    } catch {
        return String(val);
    }
};

const formatPeriodLabel = (period) => {
    if (!period || typeof period !== 'object') return '—';
    return `${formatDate(period.startDate)} – ${formatDate(period.endDateExclusive)}`;
};

const MonthlyReportPanel = ({ serverID }) => {
    const [selectedPeriod, setSelectedPeriod] = useState('');
    const [periods, setPeriods] = useState([]);
    const [olderCursor, setOlderCursor] = useState(null);
    const [periodsBusy, setPeriodsBusy] = useState(false);
    const [periodError, setPeriodError] = useState(null);

    // Active Snapshot Report State
    const [report, setReport] = useState(null);
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    // UI State
    const [activeSubTab, setActiveSubTab] = useState('report'); // 'report' | 'email'
    const [searchQuery, setSearchQuery] = useState('');
    const [filterKind, setFilterKind] = useState('ALL');
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 20;

    // Row Detail Modal
    const [detailRow, setDetailRow] = useState(null);

    // Save Snapshot Modal & Progress
    const [savingSnapshot, setSavingSnapshot] = useState(false);
    const [snapshotResult, setSnapshotResult] = useState(null);

    // 1. Load Available Periods
    const loadPeriods = useCallback(async (older = false) => {
        if (serverID !== '360camhs.co.uk') {
            setPeriods([]);
            setOlderCursor(null);
            setPeriodsBusy(false);
            setPeriodError(null);
            return;
        }

        setPeriodsBusy(true);
        setPeriodError(null);
        const cursor = older ? olderCursor || '' : '';

        try {
            const res = await listBleepMonthlySnapshots(serverID, cursor);
            const snapshots = Array.isArray(res?.snapshots) ? res.snapshots : [];

            setPeriods((prev) => {
                const map = new Map();
                if (older) {
                    prev.forEach((p) => map.set(p.snapshotID, p));
                }
                snapshots.forEach((p) => {
                    if (p.snapshotID) map.set(p.snapshotID, p);
                });
                return Array.from(map.values()).sort((a, b) => b.snapshotID.localeCompare(a.snapshotID));
            });

            setOlderCursor(res?.hasMore ? res?.nextSnapshotID : null);
        } catch (err) {
            console.error('Failed to load snapshot periods:', err);
            setPeriodError(err?.message || 'Saved periods could not be loaded.');
        } finally {
            setPeriodsBusy(false);
        }
    }, [serverID, olderCursor]);

    // 2. Load Snapshot Data with complete pagination
    const loadReport = useCallback(async () => {
        setBusy(true);
        setError(null);
        setReport(null);
        setRows([]);
        setPage(0);

        try {
            if (serverID !== '360camhs.co.uk') {
                throw new Error('Saved monthly account-activity snapshots are currently deployed for 360 CAMHS. Monthly licence auditing and combined multi-division checks below are active for all servers.');
            }

            let snapshotID = selectedPeriod.trim();
            let cursor = '';
            let checksum = null;
            let header = null;
            const collectedRows = [];
            const seenRowIDs = new Set();
            const seenCursors = new Set();

            do {
                const response = await getBleepMonthlySnapshot({
                    serverID,
                    snapshotID,
                    afterRowID: cursor,
                });

                if (!response || response.success !== true) {
                    throw new Error('Report read was not confirmed.');
                }
                if (response.found !== true) {
                    throw new Error(header == null
                        ? 'No completed report is available for this period yet.'
                        : 'The snapshot became unavailable while loading.');
                }

                const returnedID = String(response.snapshotID || '').trim();
                if (!returnedID || (snapshotID && snapshotID !== returnedID)) {
                    throw new Error('Snapshot identity changed while loading.');
                }
                snapshotID = returnedID; // Pin subsequent pages to same snapshot

                const returnedChecksum = String(response.checksum || '').trim();
                if (!returnedChecksum || (checksum && checksum !== returnedChecksum)) {
                    throw new Error('Snapshot checksum changed while loading.');
                }
                checksum = returnedChecksum;

                if (!header) {
                    header = { ...response };
                    delete header.rows;
                    delete header.hasMore;
                    delete header.nextRowID;
                }

                const pageRows = Array.isArray(response.rows) ? response.rows : [];
                for (const r of pageRows) {
                    const rId = String(r.rowID || '');
                    if (!rId || seenRowIDs.has(rId)) {
                        throw new Error('Duplicate or invalid report row encountered.');
                    }
                    seenRowIDs.add(rId);
                    collectedRows.push(r);
                }

                if (response.hasMore !== true) break;

                cursor = String(response.nextRowID || '').trim();
                if (!cursor || seenCursors.has(cursor) || seenCursors.size > 2000) {
                    throw new Error('Invalid report pagination.');
                }
                seenCursors.add(cursor);
            } while (true);

            // Verify row count against summary if available
            const expectedCount = header?.summary?.rowCount;
            if (typeof expectedCount === 'number' && collectedRows.length !== expectedCount) {
                console.warn(`Row count mismatch: expected ${expectedCount}, got ${collectedRows.length}`);
            }

            // Sort chronologically (latest first), then by employeeID
            collectedRows.sort((a, b) => {
                const byDate = String(b.eventAt || '').localeCompare(String(a.eventAt || ''));
                if (byDate !== 0) return byDate;
                return String(a.employeeID || '').localeCompare(String(b.employeeID || ''));
            });

            setReport(header);
            setRows(collectedRows);
        } catch (err) {
            console.error('Failed to load snapshot report:', err);
            setError(err?.message || 'Report unavailable. Please retry.');
        } finally {
            setBusy(false);
        }
    }, [serverID, selectedPeriod]);

    // Initial load on server or period change
    useEffect(() => {
        loadPeriods(false);
    }, [serverID]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    // 3. Save Last Closed Snapshot (CAMHS only)
    const handleSaveSnapshot = async () => {
        if (serverID !== '360camhs.co.uk' || savingSnapshot) return;
        setSavingSnapshot(true);

        try {
            const res = await saveMonthlySnapshot(serverID);
            if (res?.success !== true) {
                throw new Error(res?.message || 'Snapshot was not confirmed.');
            }
            setSnapshotResult(res);
            // Reload periods and report
            await loadPeriods(false);
            if (res.snapshotID) {
                setSelectedPeriod(res.snapshotID);
            }
        } catch (err) {
            console.error('Failed to save monthly snapshot:', err);
            alert(`Could not save monthly snapshot: ${err?.message || err}`);
        } finally {
            setSavingSnapshot(false);
        }
    };

    // Filtered Activity Rows
    const activityRows = useMemo(() => {
        return rows.filter((r) => ['ACCOUNT_ADDED', 'HA_TERMINATED', 'HA_RESTORED'].includes(r.kind));
    }, [rows]);

    const visibleRows = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return activityRows.filter((r) => {
            if (filterKind !== 'ALL' && r.kind !== filterKind) return false;
            if (q) {
                const searchable = `${r.employeeName || ''} ${r.employeeID || ''} ${r.candidateReference || ''} ${r.performedByAdminID || ''} ${r.reason || ''}`.toLowerCase();
                if (!searchable.includes(q)) return false;
            }
            return true;
        });
    }, [activityRows, filterKind, searchQuery]);

    const paginatedRows = useMemo(() => {
        const start = page * PAGE_SIZE;
        return visibleRows.slice(start, start + PAGE_SIZE);
    }, [visibleRows, page]);

    const totalPages = Math.ceil(visibleRows.length / PAGE_SIZE) || 1;

    // Export Activity CSV (matching Flutter format)
    const handleExportActivityCsv = () => {
        if (!report || activityRows.length === 0) return;

        const period = report.period || {};
        const heading = [
            'Period Start (UK)',
            'Period End Exclusive (UK)',
            'Division',
            'Report Status',
            'Change',
            'Employee Name',
            'Candidate Reference',
            'Event Date (UTC)',
            'Changed By',
        ];

        const kindMap = {
            ACCOUNT_ADDED: 'Added',
            HA_TERMINATED: 'Terminated',
            HA_RESTORED: 'Restored',
        };

        const csvRows = [heading];
        activityRows.forEach((r) => {
            csvRows.push([
                period.startDate || '',
                period.endDateExclusive || '',
                report.serverID || serverID,
                'Provisional account activity — not a billing statement',
                kindMap[r.kind] || r.kind || '',
                r.employeeName || 'Name not recorded',
                r.candidateReference || '',
                r.eventAt || '',
                r.performedByAdminID || 'Not recorded',
            ]);
        });

        // Add UTF-8 BOM (\uFEFF) for Excel
        const csvContent = '\uFEFF' + csvRows.map((row) => row.map(sanitizeCsvCell).join(',')).join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `monthly_account_activity_${serverID}_${report.snapshotID || 'current'}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const summary = report?.summary || {};

    return (
        <div style={{ paddingBottom: '30px' }}>
            {/* Top Toolbar Card: Period Picker & Actions */}
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                padding: '20px 24px',
                marginBottom: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px',
                }}>
                    {/* Period Selector */}
                    <div style={{ flex: '1 1 340px', minWidth: '280px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '700', color: '#4a5568', display: 'block', marginBottom: '6px' }}>
                            Reporting Period (23rd to 23rd · UK midnight · End date excluded):
                        </label>
                        <select
                            value={selectedPeriod}
                            onChange={(e) => {
                                setSelectedPeriod(e.target.value);
                            }}
                            disabled={busy}
                            style={{
                                width: '100%',
                                padding: '10px 14px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#2b6cb0',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e0',
                                backgroundColor: '#ffffff',
                                outline: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            <option value="">Latest completed report</option>
                            {periods.map((p) => (
                                <option key={p.snapshotID} value={p.snapshotID}>
                                    {p.period ? formatPeriodLabel(p.period) : p.snapshotID} ({p.snapshotID})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            onClick={() => {
                                loadReport();
                                loadPeriods(false);
                            }}
                            disabled={busy || periodsBusy}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: '#edf2f7',
                                color: '#2b6cb0',
                                border: '1px solid #cbd5e0',
                                padding: '10px 16px',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: busy || periodsBusy ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <FaSync className={busy || periodsBusy ? 'fa-spin' : ''} />
                            <span>Refresh</span>
                        </button>

                        {/* Save Last Closed Snapshot Button (CAMHS only) */}
                        {serverID === '360camhs.co.uk' && (
                            <button
                                type="button"
                                onClick={handleSaveSnapshot}
                                disabled={savingSnapshot || busy}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#ffffff',
                                    color: '#176B64',
                                    border: '1px solid #176B64',
                                    padding: '10px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: savingSnapshot || busy ? 'not-allowed' : 'pointer',
                                }}
                                title="Builds and freezes the closed monthly snapshot for CAMHS"
                            >
                                <FaCamera />
                                <span>{savingSnapshot ? 'Building snapshot…' : 'Save Last Closed Snapshot'}</span>
                            </button>
                        )}

                        {/* Export Activity CSV Button (Active in report tab) */}
                        {activeSubTab === 'report' && (
                            <button
                                type="button"
                                onClick={handleExportActivityCsv}
                                disabled={busy || !report || activityRows.length === 0}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#176B64',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '10px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: busy || !report || activityRows.length === 0 ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 2px 4px rgba(23, 107, 100, 0.25)',
                                }}
                            >
                                <FaDownload />
                                <span>Export Activity CSV</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Cursor Pagination for older periods */}
                {olderCursor && (
                    <div style={{ marginTop: '10px' }}>
                        <button
                            type="button"
                            onClick={() => loadPeriods(true)}
                            disabled={periodsBusy}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#2b6cb0',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                padding: 0,
                                textDecoration: 'underline',
                            }}
                        >
                            {periodsBusy ? 'Loading older periods…' : 'Load older periods'}
                        </button>
                    </div>
                )}

                {periodError && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#c53030', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{periodError}</span>
                        <button
                            type="button"
                            onClick={() => loadPeriods(false)}
                            style={{ background: 'none', border: 'none', color: '#2b6cb0', cursor: 'pointer', textDecoration: 'underline', fontSize: '12px' }}
                        >
                            Retry
                        </button>
                    </div>
                )}
            </div>

            {/* Sub-Tab Navigation: Report vs Email Delivery */}
            <div style={{
                display: 'flex',
                gap: '8px',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '20px',
            }}>
                <button
                    type="button"
                    onClick={() => setActiveSubTab('report')}
                    style={{
                        padding: '10px 18px',
                        fontSize: '14px',
                        fontWeight: '700',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: activeSubTab === 'report' ? '#176B64' : '#718096',
                        borderBottom: activeSubTab === 'report' ? '3px solid #176B64' : '3px solid transparent',
                        marginBottom: '-2px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <FaFileInvoiceDollar />
                    <span>Report & Licence Billing</span>
                </button>

                <button
                    type="button"
                    onClick={() => setActiveSubTab('email')}
                    style={{
                        padding: '10px 18px',
                        fontSize: '14px',
                        fontWeight: '700',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: activeSubTab === 'email' ? '#176B64' : '#718096',
                        borderBottom: activeSubTab === 'email' ? '3px solid #176B64' : '3px solid transparent',
                        marginBottom: '-2px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <FaEnvelope />
                    <span>Email Delivery Audit</span>
                </button>
            </div>

            {/* Error Message */}
            {error && (
                <div style={{
                    padding: '14px 18px',
                    backgroundColor: '#fff5f5',
                    color: '#c53030',
                    border: '1px solid #fed7d7',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                }}>
                    <FaExclamationTriangle style={{ fontSize: '18px', flexShrink: 0 }} />
                    <div>
                        <div style={{ fontWeight: '700' }}>Could not load snapshot report</div>
                        <div style={{ fontSize: '13px', marginTop: '2px' }}>{error}</div>
                    </div>
                </div>
            )}

            {/* Loading Indicator */}
            {busy && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
                    <FaSpinner className="fa-spin" style={{ fontSize: '32px', color: '#176B64', marginBottom: '12px' }} />
                    <p style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>
                        Loading saved monthly snapshot report…
                    </p>
                </div>
            )}

            {/* SUB-TAB 1: REPORT */}
            {!busy && activeSubTab === 'report' && (
                <div>
                    {/* Period Banner Header */}
                    {report?.period && (
                        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaCalendarAlt style={{ color: '#176B64' }} />
                            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#2d3748' }}>
                                Period: {formatPeriodLabel(report.period)}
                            </h3>
                            <span style={{ fontSize: '12px', color: '#718096' }}>
                                ({report.snapshotID})
                            </span>
                        </div>
                    )}

                    {/* Snapshot Summary Metric Cards */}
                    {report && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                            gap: '16px',
                            marginBottom: '24px',
                        }}>
                            <div style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '10px',
                                padding: '18px 20px',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#176B64', marginBottom: '8px' }}>
                                    <FaUserPlus />
                                    <span style={{ fontSize: '13px', fontWeight: '700' }}>Accounts Added</span>
                                </div>
                                <div style={{ fontSize: '32px', fontWeight: '800', color: '#2d3748' }}>
                                    {summary.accountAdditions ?? 0}
                                </div>
                                <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                    New employee accounts created
                                </div>
                            </div>

                            <div style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '10px',
                                padding: '18px 20px',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#C53030', marginBottom: '8px' }}>
                                    <FaUserMinus />
                                    <span style={{ fontSize: '13px', fontWeight: '700' }}>Terminations</span>
                                </div>
                                <div style={{ fontSize: '32px', fontWeight: '800', color: '#2d3748' }}>
                                    {summary.terminationEvents ?? 0}
                                </div>
                                <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                    Inactive employee offboardings
                                </div>
                            </div>

                            <div style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '10px',
                                padding: '18px 20px',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2B6CB0', marginBottom: '8px' }}>
                                    <FaUserShield />
                                    <span style={{ fontSize: '13px', fontWeight: '700' }}>Restorations</span>
                                </div>
                                <div style={{ fontSize: '32px', fontWeight: '800', color: '#2d3748' }}>
                                    {summary.restorationEvents ?? 0}
                                </div>
                                <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                    Reverted termination events
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Embedded Monthly Licence Section (De-duplication, Contract baseline 350, Official CSV) */}
                    <MonthlyLicenceSection
                        key={`licence-${serverID}-${report?.period?.endDateExclusive || ''}`}
                        serverID={serverID}
                        endDate={report?.period?.endDateExclusive || ''}
                    />

                    {/* Account Activity Table Panel */}
                    {report && (
                        <div style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            padding: '24px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '12px',
                                marginBottom: '16px',
                            }}>
                                <div>
                                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#2d3748' }}>
                                        Account Activity Log
                                    </h4>
                                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#718096' }}>
                                        Includes migrated records. These additions represent changes in the period and are not the chargeable licence total.
                                    </p>
                                </div>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#176B64' }}>
                                    {visibleRows.length} change{visibleRows.length !== 1 ? 's' : ''} recorded
                                </span>
                            </div>

                            {/* Search & Filter Chips */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '12px',
                                marginBottom: '16px',
                            }}>
                                <div style={{ position: 'relative', width: '340px', maxWidth: '100%' }}>
                                    <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#a0aec0' }} />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                                        placeholder="Search employee, reference, or admin ID…"
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px 8px 34px',
                                            fontSize: '13px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e0',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            onClick={() => { setSearchQuery(''); setPage(0); }}
                                            style={{
                                                position: 'absolute',
                                                right: '10px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: 'none',
                                                border: 'none',
                                                color: '#a0aec0',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <FaTimes />
                                        </button>
                                    )}
                                </div>

                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {['ALL', 'ACCOUNT_ADDED', 'HA_TERMINATED', 'HA_RESTORED'].map((k) => {
                                        const count = k === 'ALL' ? activityRows.length : activityRows.filter((r) => r.kind === k).length;
                                        const isSelected = filterKind === k;
                                        return (
                                            <button
                                                key={k}
                                                type="button"
                                                onClick={() => { setFilterKind(k); setPage(0); }}
                                                style={{
                                                    padding: '5px 12px',
                                                    borderRadius: '20px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    border: `1px solid ${isSelected ? '#176B64' : '#e2e8f0'}`,
                                                    backgroundColor: isSelected ? '#E5F2EF' : '#ffffff',
                                                    color: isSelected ? '#176B64' : '#4a5568',
                                                }}
                                            >
                                                {MONTHLY_SNAPSHOT_KINDS[k] || k} ({count})
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Table */}
                            <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                            <th style={{ padding: '10px 14px', color: '#4a5568', fontWeight: '700' }}>EMPLOYEE</th>
                                            <th style={{ padding: '10px 14px', color: '#4a5568', fontWeight: '700' }}>CHANGE</th>
                                            <th style={{ padding: '10px 14px', color: '#4a5568', fontWeight: '700' }}>DATE · UTC</th>
                                            <th style={{ padding: '10px 14px', color: '#4a5568', fontWeight: '700' }}>RECORDED BY</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedRows.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} style={{ padding: '30px', textAlign: 'center', color: '#a0aec0' }}>
                                                    No account activity records match your filter criteria.
                                                </td>
                                            </tr>
                                        ) : (
                                            paginatedRows.map((row, idx) => {
                                                const isTerm = row.kind === 'HA_TERMINATED';
                                                const isAdd = row.kind === 'ACCOUNT_ADDED';
                                                const isRest = row.kind === 'HA_RESTORED';

                                                return (
                                                    <tr
                                                        key={row.rowID || idx}
                                                        onClick={() => setDetailRow(row)}
                                                        style={{
                                                            borderBottom: '1px solid #edf2f7',
                                                            cursor: 'pointer',
                                                            transition: 'background-color 0.15s ease',
                                                        }}
                                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                    >
                                                        <td style={{ padding: '12px 14px' }}>
                                                            <div style={{ fontWeight: '600', color: '#2d3748' }}>
                                                                {row.employeeName || 'Name not recorded'}
                                                            </div>
                                                            <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
                                                                Ref: {row.candidateReference || row.employeeID || '—'}
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '12px 14px' }}>
                                                            <span style={{
                                                                backgroundColor: isTerm ? '#FFF5F5' : isAdd ? '#E5F2EF' : '#EBF8FF',
                                                                color: isTerm ? '#C53030' : isAdd ? '#176B64' : '#2B6CB0',
                                                                padding: '3px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '11px',
                                                                fontWeight: '700',
                                                            }}>
                                                                {isTerm ? 'Terminated' : isAdd ? 'Added' : isRest ? 'Restored' : row.kind}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '12px 14px', color: '#4a5568' }}>
                                                            {formatDate(row.eventAt, true)}
                                                        </td>
                                                        <td style={{ padding: '12px 14px', color: '#718096', fontSize: '12px' }}>
                                                            {row.performedByAdminID || 'Not recorded'}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Bar */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: '14px',
                                fontSize: '12px',
                                color: '#718096',
                            }}>
                                <span>
                                    Showing {visibleRows.length === 0 ? 0 : page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, visibleRows.length)} of {visibleRows.length} records
                                </span>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <button
                                        type="button"
                                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                                        disabled={page === 0}
                                        style={{
                                            padding: '4px 10px',
                                            borderRadius: '4px',
                                            border: '1px solid #cbd5e0',
                                            background: page === 0 ? '#f7fafc' : '#ffffff',
                                            color: page === 0 ? '#a0aec0' : '#4a5568',
                                            cursor: page === 0 ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        <FaChevronLeft style={{ fontSize: '10px' }} /> Previous
                                    </button>
                                    <span>{page + 1} / {totalPages}</span>
                                    <button
                                        type="button"
                                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                                        disabled={page >= totalPages - 1}
                                        style={{
                                            padding: '4px 10px',
                                            borderRadius: '4px',
                                            border: '1px solid #cbd5e0',
                                            background: page >= totalPages - 1 ? '#f7fafc' : '#ffffff',
                                            color: page >= totalPages - 1 ? '#a0aec0' : '#4a5568',
                                            cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        Next <FaChevronRight style={{ fontSize: '10px' }} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* SUB-TAB 2: EMAIL DELIVERY */}
            {!busy && activeSubTab === 'email' && (
                <MonthlyEmailAuditSection
                    key={`email-${serverID}-${report?.snapshotID || selectedPeriod || 'none'}`}
                    serverID={serverID}
                    periodID={report?.snapshotID || selectedPeriod || ''}
                    reportChecksum=""
                />
            )}

            {/* Change Detail Dialog Modal */}
            {detailRow && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '520px',
                        width: '100%',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#2d3748' }}>
                                {detailRow.employeeName || 'Name not recorded'}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setDetailRow(null)}
                                style={{ background: 'none', border: 'none', fontSize: '18px', color: '#a0aec0', cursor: 'pointer' }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13px' }}>
                            <div>
                                <span style={{ color: '#718096', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: '700' }}>
                                    Candidate Reference
                                </span>
                                <span style={{ color: '#2d3748', fontWeight: '600' }}>{detailRow.candidateReference || '—'}</span>
                            </div>
                            <div>
                                <span style={{ color: '#718096', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: '700' }}>
                                    Employee ID
                                </span>
                                <code style={{ backgroundColor: '#edf2f7', padding: '2px 6px', borderRadius: '4px' }}>
                                    {detailRow.employeeID || '—'}
                                </code>
                            </div>
                            <div>
                                <span style={{ color: '#718096', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: '700' }}>
                                    Change Kind
                                </span>
                                <span style={{ fontWeight: '700', color: '#176B64' }}>{detailRow.kind}</span>
                            </div>
                            <div>
                                <span style={{ color: '#718096', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: '700' }}>
                                    Event Time (UTC)
                                </span>
                                <span>{formatDate(detailRow.eventAt, true)}</span>
                            </div>
                            <div>
                                <span style={{ color: '#718096', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: '700' }}>
                                    Recorded Admin
                                </span>
                                <span>{detailRow.performedByAdminID || 'Not recorded'}</span>
                            </div>
                            {detailRow.reason && (
                                <div>
                                    <span style={{ color: '#718096', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: '700' }}>
                                        Reason Note
                                    </span>
                                    <span>{detailRow.reason}</span>
                                </div>
                            )}
                        </div>

                        <div style={{ marginTop: '20px', textAlign: 'right' }}>
                            <button
                                type="button"
                                onClick={() => setDetailRow(null)}
                                style={{
                                    backgroundColor: '#edf2f7',
                                    color: '#4a5568',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Snapshot Saved Result Modal */}
            {snapshotResult && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '560px',
                        width: '100%',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#176B64' }}>
                                {snapshotResult.alreadyCompleted ? 'Existing Monthly Snapshot' : 'Monthly Snapshot Saved'}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setSnapshotResult(null)}
                                style={{ background: 'none', border: 'none', fontSize: '18px', color: '#a0aec0', cursor: 'pointer' }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div style={{ fontSize: '13px', color: '#4a5568', lineHeight: '1.6' }}>
                            <p style={{ margin: '0 0 10px 0' }}>
                                <strong>Period: </strong> {snapshotResult.period?.startDate} to {snapshotResult.period?.endDateExclusive} (End excluded · UK time)
                            </p>
                            <div style={{ backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #edf2f7', marginBottom: '12px' }}>
                                <div>• Account additions: <strong>{snapshotResult.summary?.accountAdditions ?? 0}</strong></div>
                                <div>• HA termination events: <strong>{snapshotResult.summary?.terminationEvents ?? 0}</strong></div>
                                <div>• HA restoration events: <strong>{snapshotResult.summary?.restorationEvents ?? 0}</strong></div>
                                <div>• Data issues: <strong>{snapshotResult.summary?.dataIssues ?? 0}</strong></div>
                            </div>
                            <p style={{ margin: '0 0 6px 0' }}>
                                <strong>Snapshot ID: </strong> <code>{snapshotResult.snapshotID}</code>
                            </p>
                            <p style={{ margin: '0 0 12px 0' }}>
                                <strong>Checksum: </strong> <code>{snapshotResult.checksum}</code>
                            </p>
                            <p style={{ fontSize: '12px', color: '#718096', margin: 0 }}>
                                This is account activity, not a billing statement. Migrated account creation dates are included. Historical changes without retained maintenance logs cannot be counted. No employee records were changed.
                            </p>
                        </div>

                        <div style={{ marginTop: '20px', textAlign: 'right' }}>
                            <button
                                type="button"
                                onClick={() => setSnapshotResult(null)}
                                style={{
                                    backgroundColor: '#176B64',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                }}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MonthlyReportPanel;
